'use client'

import { useState, useEffect, useRef } from 'react'
import { MenuIcon, XIcon } from 'lucide-react'
import { MobileNav } from './MobileNav.client'
import { Lektionen } from '@/payload-types'

interface MobileNavWrapperProps {
  lessons: Lektionen[]
  slug: string
  currentLection: string
  currentSection: string
  academyTitle?: string | null
}

export function MobileNavWrapper({
  lessons,
  slug,
  currentLection,
  currentSection,
  academyTitle,
}: MobileNavWrapperProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [buttonPosition, setButtonPosition] = useState({ top: 300, left: 0, width: 100, height: 0 })

  const handleOpen = () => {
    if (isOpen) {
      handleClose()
      return
    }

    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setButtonPosition({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      })
    }
    setIsOpen(true)
  }

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true)
        })
      })
    } else {
      setIsAnimating(false)
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const handleClose = () => {
    setIsAnimating(false)
    setTimeout(() => setIsOpen(false), 300)
  }

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleOpen}
        className="flex h-full items-center justify-center rounded p-2 text-primary transition-all duration-300 hover:bg-primary/10 lg:hidden"
        aria-label="Navigation öffnen"
      >
        <div className="relative h-6 w-6">
          <MenuIcon
            className={`absolute inset-0 h-6 w-6 transition-all duration-300 ${
              isOpen ? 'rotate-90 opacity-0' : 'rotate-0 opacity-100'
            }`}
          />
          <XIcon
            className={`absolute inset-0 h-6 w-6 transition-all duration-300 ${
              isOpen ? 'rotate-0 opacity-100' : '-rotate-90 opacity-0'
            }`}
          />
        </div>
      </button>

      {isOpen && (
        <MobileNav
          lessons={lessons}
          slug={slug}
          currentLection={currentLection}
          currentSection={currentSection}
          academyTitle={academyTitle}
          isAnimating={isAnimating}
          buttonPosition={buttonPosition}
          onClose={handleClose}
        />
      )}
    </>
  )
}
